import { Suspense } from 'react';
import { Await, useLoaderData } from 'react-router-dom';
import { Heading, Link, Paragraph } from '@quickmd-llc/design-system';
import { Layout, Loader } from '@components';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib/events';
import { AppointmentTimePreferenceLoader } from '@router/loaders/appointmentTimePreferenceLoader/appointmentTimePreferenceLoader';
import { EMAIL_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import { ProviderAvailability } from '@types';
import { AvailabilitySelector } from './AvailabilitySelector/AvailabilitySelector';
import { AppointmentTimePreferenceProvider } from './context/AppointmentTimePreferenceProvider';
import { TimeBlock, useParseTimeBlockSearchParam } from './context/utils';
import { TimeBlockSelector } from './TimeBlockSelector';
import { SearchQuery } from '../components';

export function AppointmentTimePreferencePage() {
  const { fetchAvailabilities } =
    useLoaderData() as AppointmentTimePreferenceLoader;
  const timeBlock = useParseTimeBlockSearchParam();
  const utmParameters = useApplicationStore((state) => state.utmParameters);

  const headerText = getHeaderText(timeBlock);

  function handleClickNotReadyToBook() {
    tracking.trackEvent(Events.CLICK_NOT_READY_TO_BOOK, {
      ...utmParameters,
      fbpixel: undefined,
    });
  }

  return (
    <Layout className="flex flex-col gap-sp-2" hideProgressBar={true}>
      <div className="grid gap-sp-2">
        <div className="grid gap-sp-0.5">
          <Heading level="h1">{headerText}</Heading>

          <Paragraph className="text-text-medium">
            Find a time that fits your schedule.
          </Paragraph>
        </div>

        <Link
          to={EMAIL_ROUTE_PATHNAME}
          onClick={handleClickNotReadyToBook}
          size="lg"
        >
          Not ready to book?
        </Link>

        <SearchQuery />
      </div>

      <Suspense fallback={<Loader />}>
        <Await resolve={fetchAvailabilities}>
          {(availabilities: ProviderAvailability[]) => (
            <ResolvedAvailabilities
              availabilities={availabilities}
              timeBlock={timeBlock}
            />
          )}
        </Await>
      </Suspense>
    </Layout>
  );
}

type Props = {
  availabilities: ProviderAvailability[];
  timeBlock: TimeBlock | null;
};

function ResolvedAvailabilities({ availabilities, timeBlock }: Props) {
  return (
    <AppointmentTimePreferenceProvider availabilities={availabilities}>
      {timeBlock ? <AvailabilitySelector /> : <TimeBlockSelector />}
    </AppointmentTimePreferenceProvider>
  );
}

function getHeaderText(timeBlock: TimeBlock | null) {
  if (timeBlock === 'now') {
    return 'Available now';
  }

  if (timeBlock === 'today') {
    return 'Available today';
  }

  if (timeBlock === 'tomorrow') {
    return 'Available tomorrow';
  }

  return 'When would you like to be seen?';
}
